import { useRef, useState } from 'react'
import type { AdminDeckDto } from '@lunary/api-client'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import { deckErrorText } from './errors'
import { useDeckBacks, useDeleteBack, useUpdateDeck, useUploadBack } from './queries'
import { ACCEPT, fileProblem, useFileDrop } from './use-file-drop'
import styles from './decks.module.css'

export function BackPanel({ deck, uploadsEnabled }: { deck: AdminDeckDto; uploadsEnabled: boolean }) {
  const input = useRef<HTMLInputElement>(null)
  const upload = useUploadBack(deck.id)
  const update = useUpdateDeck(deck.id)
  const backs = useDeckBacks()
  const remove = useDeleteBack()
  const [doomed, setDoomed] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const send = (file: File) => {
    const bad = fileProblem(file)
    setProblem(bad)
    if (!bad) upload.mutate(file)
  }
  const drop = useFileDrop((files) => {
    if (files[0]) send(files[0])
  }, !uploadsEnabled || upload.isPending)
  const library = backs.data?.items ?? []

  return (
    <section className={styles.sideBlock} aria-labelledby="deck-back">
      <div className={styles.sideHead}>
        <h3 id="deck-back">Рубашка</h3>
        <span className={styles.sub}>одна на колоду</span>
      </div>
      <div className={styles.back}>
        <div className={styles.backDrop} data-over={drop.over} data-state={upload.isPending ? 'busy' : problem ? 'bad' : undefined} {...drop.bind}>
          {deck.back ? <img className={styles.backImg} src={deck.back.url} alt="Рубашка колоды" draggable={false} /> : <div className={styles.backImg}>Стандартная</div>}
        </div>
        <div className={styles.backActions}>
          <p className={styles.hint} style={{ margin: 0 }}>
            Перетащите картинку на рубашку. JPEG, PNG, WebP до 8 МБ, 2:3.
          </p>
          <div className={styles.actions}>
            <button type="button" className="secondary" disabled={!uploadsEnabled || upload.isPending} onClick={() => input.current?.click()}>
              {upload.isPending ? 'Загружаю…' : deck.back ? 'Заменить' : 'Загрузить'}
            </button>
            <input
              ref={input}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (file) send(file)
              }}
            />
          </div>
          {library.length > 0 && (
            <div>
              <div className={styles.sub} style={{ marginBottom: 6 }}>
                Мои рубашки
              </div>
              <div className={styles.backLib}>
                {library.map((b) => (
                  <div key={b.id} className={styles.backItem}>
                    <button
                      type="button"
                      className={styles.backPick}
                      aria-label="Выбрать рубашку"
                      aria-pressed={deck.back?.id === b.id}
                      disabled={update.isPending}
                      onClick={() => update.mutate({ back_id: b.id })}
                    >
                      <img src={b.thumb_url} alt="" draggable={false} />
                    </button>
                    <button type="button" className={styles.backRemove} aria-label="Удалить рубашку" disabled={remove.isPending} onClick={() => setDoomed(b.id)}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!uploadsEnabled && <p className={styles.error}>Хранилище картинок не настроено, загрузка недоступна.</p>}
          {problem && <p className={styles.error}>{problem}</p>}
          {(upload.isError || update.isError) && <p className={styles.error}>{deckErrorText(upload.error ?? update.error)}</p>}
        </div>
      </div>
      <ConfirmDialog
        open={doomed !== null}
        title="Удалить рубашку?"
        text="Рубашка пропадёт из библиотеки. Колоды с ней перейдут на стандартную анимированную рубашку."
        confirmLabel="Удалить"
        danger
        pending={remove.isPending}
        error={remove.isError ? deckErrorText(remove.error) : null}
        onClose={() => setDoomed(null)}
        onConfirm={() => {
          if (doomed) remove.mutate(doomed, { onSuccess: () => setDoomed(null) })
        }}
      />
    </section>
  )
}
